import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Undo2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { LoanTable, ChangeList } from "./LoanTable";
import { ConfirmDialog } from "./ConfirmDialog";

const CONF = {
  high: "bg-[#D1FAE5] text-[#065F46]",
  medium: "bg-[#DBEAFE] text-[#1E40AF]",
  low: "bg-[#F3F4F6] text-[#374151]",
};
const BASE = "/operations/payoffs/client-groups/review";

const Side = ({ label, side, testid }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide text-[#6B7280] mb-1">
      {label} · <span className="font-mono normal-case">{side.group_id || `${side.unit_id} (not yet grouped)`}</span>
    </div>
    <LoanTable rows={side.loans} testid={testid} />
  </div>
);

const SuggestionCard = ({ s, onDecide, rejected, onUnreject }) => (
  <div className="border border-[#E5E7EB] rounded-xl p-4 bg-white space-y-3" data-testid={`suggestion-${s.suggestion_key}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`font-semibold px-2 py-0.5 rounded uppercase ${CONF[s.confidence]}`} data-testid={`suggestion-confidence-${s.suggestion_key}`}>{s.confidence}</span>
        <span className="font-mono text-[#9CA3AF]">{s.suggestion_key}</span>
      </div>
      {rejected ? (
        <div className="flex items-center gap-2 text-xs text-[#6B7280]">
          Rejected by {s.decision?.by_email} {s.decision?.note && `— “${s.decision.note}”`}
          <Button size="sm" variant="outline" onClick={() => onUnreject(s)} data-testid={`unreject-btn-${s.suggestion_key}`}><Undo2 className="w-3.5 h-3.5 mr-1" />Undo reject</Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onDecide(s, "reject")} data-testid={`reject-btn-${s.suggestion_key}`}>Reject — different clients</Button>
          <Button size="sm" onClick={() => onDecide(s, "approve")} data-testid={`approve-btn-${s.suggestion_key}`} className="bg-[#0066FF] hover:bg-[#0052CC] text-white">Approve merge…</Button>
        </div>
      )}
    </div>
    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
      {s.signals.map((x) => <span key={x} className="flex items-center gap-1 text-[#065F46]"><CheckCircle2 className="w-3.5 h-3.5" />{x}</span>)}
      {s.warnings.map((x) => <span key={x} className="flex items-center gap-1 text-[#92400E]"><AlertTriangle className="w-3.5 h-3.5" />{x}</span>)}
    </div>
    <div className="grid grid-cols-1 gap-3">
      <Side label="Side A" side={s.side_a} testid={`suggestion-side-a-${s.suggestion_key}`} />
      <Side label="Side B" side={s.side_b} testid={`suggestion-side-b-${s.suggestion_key}`} />
    </div>
  </div>
);

export const ReviewSuggestionsPanel = ({ plan, onChanged }) => {
  const [pending, setPending] = useState(null); // {s, action}
  const [showRejected, setShowRejected] = useState(false);

  const decide = async (note) => {
    const { s, action } = pending;
    try {
      const { data } = await api.post(`${BASE}/${action}`, { suggestion_key: s.suggestion_key, note });
      toast.success(action === "approve" ? `Merged into ${data.target_group_id}` : "Suggestion rejected — no loans changed");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };
  const unreject = async (s) => {
    try { await api.post(`${BASE}/unreject`, { suggestion_key: s.suggestion_key }); toast.success("Back in the review queue"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const s = pending?.s;
  const approving = pending?.action === "approve";
  return (
    <div className="space-y-4" data-testid="review-suggestions-panel">
      <div className="p-4 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#374151]">
        <div className="font-semibold text-[#0A2540]">Tier 2: needs a human decision</div>
        <p className="mt-1">These might be the same client, but they don't pass the strict safe rules (for example a different middle name, a typo, a shared email, or a missing/different firm_id). Nothing here changes until you approve each one yourself. Approving changes only <b>client_group_id</b>. Firm assignments, balances and payoff math for each loan stay separate.</p>
      </div>
      {plan.review_suggestions.length === 0 && <div className="text-sm text-[#6B7280]" data-testid="review-empty">No open suggestions.</div>}
      {plan.review_suggestions.map((x) => <SuggestionCard key={x.suggestion_key} s={x} onDecide={(sg, action) => setPending({ s: sg, action })} />)}

      {plan.rejected_suggestions.length > 0 && (
        <button className="text-sm text-[#0066FF]" onClick={() => setShowRejected(!showRejected)} data-testid="toggle-rejected-btn">
          {showRejected ? "Hide" : "Show"} {plan.rejected_suggestions.length} rejected suggestion(s)
        </button>
      )}
      {showRejected && plan.rejected_suggestions.map((x) => <SuggestionCard key={x.suggestion_key} s={x} rejected onUnreject={unreject} />)}

      <ConfirmDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)} testid="decision-dialog"
        title={approving ? "Approve merge into one client group?" : "Reject this suggestion?"}
        description={approving
          ? `The loans below will share client group ${s?.target_group_id || "(new group minted on approval)"}. Each loan stays its own record with its own balance. firm_id is not changed.`
          : "No loans are changed. This pairing is hidden from the queue, and you can undo that later."}
        confirmLabel={approving ? "Approve & merge" : "Reject"} danger={!approving}
        requireNote={approving} notePlaceholder={approving ? "Why are these the same client? (required)" : "Optional reason"}
        onConfirm={decide}>
        {approving && s && (
          <>
            <div className="text-xs font-medium text-[#374151]">client_group_id changes ({s.changes.length})</div>
            <ChangeList changes={s.changes} testid="decision-change-list" />
            {s.warnings.length > 0 && (
              <div className="text-xs text-[#92400E] bg-[#FFFBEB] border border-[#FCD34D] rounded-lg p-2 space-y-0.5">
                {s.warnings.map((w) => <div key={w}>• {w}</div>)}
              </div>
            )}
          </>
        )}
      </ConfirmDialog>
    </div>
  );
};
